'use client'
import { useTranslate } from '@repo/shared/hooks'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Row,
  Col,
  Typography,
  Icon,
  Separator,
} from '@repo/ui/components'
import { ValueWithUnit } from '../value-with-unit'
import type { FlatAsset, FlatOrderData } from './order-box.types'
// import { formatNumberWithCommasAndDecimals } from '@repo/tools'
// import { OrderBoxReferral } from './order-box.referral'
import type { ReferralData } from './order-box.referral.types'
import { OrderBoxPriceTimer } from './order-box.price-timer'
import { useState, useEffect } from 'react'
import type { UserOTCReferral } from '@repo/services'

export interface OrderBoxConvertDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fromAsset?: FlatAsset
  toAsset?: FlatAsset
  fromAmount: number
  toAmount: number
  conversionRate?: number
  onConfirm: (referralCode?: string) => void
  isLoading?: boolean
  targetScale?: number
  targetAmount?: number
  defaultReferralCode?: string
  flatOrderData?: FlatOrderData
  onReferralChange?: (
    referralData: ReferralData | null,
    callback: () => void,
  ) => void
  hasDefaultReferral?: boolean
  onCheckReferralCode?: (
    referralCode: string,
    fromAmount: number,
    onSuccess: (data: ReferralData) => void,
    onError: (error: any) => void,
  ) => void
  isPriceValid?: boolean
  onTimeExpired?: () => void
  onRefreshPrice?: () => void
  onRefreshConvertPrice?: (
    fromAmount: number,
    referralCode?: string,
    onSuccess?: () => void,
  ) => void
  referal?: UserOTCReferral
}

export const OrderBoxConvertDialog = (props: OrderBoxConvertDialogProps) => {
  const {
    open,
    onOpenChange,
    fromAsset,
    toAsset,
    fromAmount,
    toAmount,
    conversionRate,
    onConfirm,
    isLoading = false,
    targetScale = 1,
    targetAmount,
    defaultReferralCode,
    flatOrderData,
    onReferralChange,
    hasDefaultReferral,
    onCheckReferralCode,
    isPriceValid = true,
    onTimeExpired,
    onRefreshPrice,
    onRefreshConvertPrice,
    referal,
  } = props

  const { t } = useTranslate()
  const [referralCode, setReferralCode] = useState<string>(
    defaultReferralCode || '',
  )

  const handleReferralChange = (
    data: ReferralData | null,
    callback: () => void,
  ) => {
    if (data) {
      setReferralCode(data.code)
      onReferralChange?.(data, callback)
    } else {
      // When referral code is removed, clear it completely (don't use defaultReferralCode)
      setReferralCode('')
      // Execute callback immediately to update UI state (setReferralData(null) and setShowReferralSection(true))
      callback()
      // Then refresh price and notify parent
      if (onRefreshConvertPrice && fromAsset && toAsset && fromAmount > 0) {
        // Call API without referral code (empty string)
        onRefreshConvertPrice(fromAmount, '', () => {
          // Price refreshed successfully without referral code
          // Notify parent about referral change
          onReferralChange?.(null, () => {})
        })
        return
      }
      // If no refresh needed, just notify parent
      onReferralChange?.(null, () => {})
    }
  }

  // Wrapper for onCheckReferralCode that uses dialog's fromAmount
  // This wrapper adapts the signature to match OrderBoxReferral's expected signature
  const handleCheckReferralCodeWrapper = (
    referralCode: string,
    fromAmountParam: number,
    onSuccess: (data: { code: string }) => void,
    onError: (error: any) => void,
  ) => {
    if (!onCheckReferralCode) {
      onError(new Error('onCheckReferralCode is not provided'))
      return
    }

    // Use the fromAmount from dialog props (which comes from form)
    // This ensures we use the correct amount even if state hasn't updated
    const amountToUse = fromAmountParam || fromAmount

    if (!fromAsset || !toAsset || amountToUse <= 0) {
      onError(
        new Error(
          'Invalid convert parameters: fromAmount must be greater than 0',
        ),
      )
      return
    }

    // Call the parent's handler with the dialog's fromAmount
    onCheckReferralCode(referralCode, amountToUse, onSuccess, onError)
  }

  // Reset referral data when dialog closes
  useEffect(() => {
    if (!open) {
      // Reset referral code state to default
      setReferralCode(defaultReferralCode || '')
      // Notify parent to reset referral data
      onReferralChange?.(null, () => {})
    }
  }, [open, defaultReferralCode, onReferralChange])

  const handleConfirm = () => {
    // Get referral code (exclude MALEK token and empty codes)
    const finalReferralCode =
      fromAsset?.token === 'MALEK' ||
      !referralCode ||
      referralCode === 'non-default'
        ? ''
        : referralCode
    onConfirm(finalReferralCode)
  }

  const handleTimeExpired = () => {
    // Handle time expiration if needed
    onTimeExpired?.()
    console.log('Price validity expired')
  }

  const fromAssetLabel =
    fromAsset?.kind === 'crypto' || fromAsset?.id === 'MALEK'
      ? 'crypto'
      : 'property'

  const toAssetLabel =
    toAsset?.kind === 'crypto' || toAsset?.id === 'MALEK'
      ? 'crypto'
      : 'property'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-full sm:max-w-[450px]'>
        <DialogHeader>
          <DialogTitle>{t('order_convert')}</DialogTitle>
        </DialogHeader>

        <Row className='items-start space-between w-full gap-xl'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap '
          >
            {t('order_convert_from')}
          </Typography>
          <Typography
            size='sm'
            color='text-neutral-main'
            weight='regular'
            className='text-left'
          >
            {fromAsset?.name || '-'}
          </Typography>
        </Row>
        <Row className='space-between w-full'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap'
          >
            {t('order_convert_token_symbol')}
          </Typography>
          <Typography size='sm' color='text-neutral-main' weight='regular'>
            {fromAsset?.token || '-'}
          </Typography>
        </Row>
        <Row className='space-between w-full'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap'
          >
            {t('order_convert_amount')}
          </Typography>
          <ValueWithUnit
            value={
              fromAsset?.kind === 'crypto'
                ? fromAmount
                : fromAmount / fromAsset?.scale!
            }
            valueSize='sm'
            valueColor='text-neutral-main'
            valueWeight='bold'
            decimalPlaces={String(fromAsset?.scale).length}
            unit={fromAssetLabel}
          />
        </Row>

        <Row className='justify-center'>
          <Icon name='ArrowDownLine' size='lg' color='fill-base-main' />
        </Row>

        <Row className='items-start space-between w-full gap-xl'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap '
          >
            {t('order_convert_to')}
          </Typography>
          <Typography
            size='sm'
            color='text-neutral-main'
            weight='regular'
            className='text-left'
          >
            {toAsset?.name || '-'}
          </Typography>
        </Row>
        <Row className='space-between w-full'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap'
          >
            {t('order_convert_token_symbol')}
          </Typography>
          <Typography size='sm' color='text-neutral-main' weight='regular'>
            {toAsset?.token || '-'}
          </Typography>
        </Row>
        <Row className='space-between w-full'>
          <Typography
            size='sm'
            color='text-neutral-surface-80'
            weight='regular'
            className='flex-1 whitespace-nowrap'
          >
            {t('order_convert_amount')}
          </Typography>
          <ValueWithUnit
            value={
              toAsset?.kind === 'crypto' ? toAmount : toAmount / toAsset?.scale!
            }
            valueSize='sm'
            valueColor='text-neutral-main'
            valueWeight='bold'
            unit={toAssetLabel}
            decimalPlaces={String(toAsset?.scale).length}
          />
        </Row>

        {/* Referral Section */}
        {/* {fromAsset && fromAsset.token !== 'MALEK' && (
          <OrderBoxReferral
            assetTitle={fromAsset.name || fromAsset.token}
            onReferralChange={handleReferralChange}
            flatOrderData={flatOrderData}
            hasDefaultReferral={!!hasDefaultReferral}
            onCheckReferralCode={handleCheckReferralCodeWrapper}
            fromAmount={fromAmount}
            referal={referal}
          />
        )} */}

        {/* Price Validity Timer */}
        <Separator />
        {isPriceValid && (
          <OrderBoxPriceTimer
            onTimeExpired={handleTimeExpired}
            initialTime={30}
          />
        )}
        {!isPriceValid && (
          <Row className='justify-center'>
            <Button
              variant='ghost'
              color='negative'
              size='sm'
              onClick={() => {
                if (onRefreshConvertPrice) {
                  // Get referral code (exclude MALEK token and empty codes)
                  const finalReferralCode =
                    fromAsset?.token === 'MALEK' ||
                    !referralCode ||
                    referralCode === 'non-default'
                      ? ''
                      : referralCode
                  onRefreshConvertPrice(fromAmount, finalReferralCode, () => {
                    // This callback will be called by the parent after API success
                    // to update isPriceValid in OrderBox
                  })
                } else {
                  onRefreshPrice?.()
                }
              }}
            >
              {t('order_price_update_required')}
            </Button>
          </Row>
        )}

        <Button
          type='button'
          size='lg'
          variant='fill'
          className='w-full mt-md'
          onClick={handleConfirm}
          disabled={!isPriceValid || isLoading}
          loading={isLoading}
        >
          {t('order_convert')}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
